import { NotificationType, SitePermitStatus } from '~/client/graph'
import type { ListSchema } from '~/client/src/shared/lists/core'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type BaseNotification from '~/client/src/shared/models/Notification'
import isUserBicForCurrentStepDefault from '~/client/src/shared/utils/isUserBicForCurrentStep'

export const READ_STATUS = {
  unread: 'unread',
  read: 'read',
  archivedUnread: 'archivedUnread',
  archivedRead: 'archivedRead',
} as const

export const LEGACY_READ_STATUS = {
  unread: 'unread',
  read: 'read',
  archived: 'archived',
} as const

export type LegacyReadStatusId =
  (typeof LEGACY_READ_STATUS)[keyof typeof LEGACY_READ_STATUS]

export const NOTIFICATION_TYPE = {
  mention: 'mention',
  bic: 'bic',
  siteComm: 'site-comm',
  update: 'update',
} as const

export const NOTIFICATION_FIELD = {
  readStatus: 'readStatus',
  notificationType: 'notificationType',
  workflowType: 'workflowType',
  workflowStatus: 'workflowStatus',
  company: 'company',
  location: 'location',
  equipment: 'equipment',
  editor: 'editor',
  searchBlob: 'searchBlob',
} as const

export const PASS_THROUGH_OPTION = '~~pass~~'

export interface INotificationPermitType {
  readonly id: string
  readonly type?: string
  readonly name?: string
}

export interface INotificationPermit {
  readonly typeId?: string
  readonly status?: SitePermitStatus
  readonly companyIds?: readonly string[]
  readonly locations?: ReadonlyArray<{ readonly id?: string }>
  readonly title?: string
  readonly code?: string
  readonly codeToDisplay?: () => string
}

export interface INotificationDelivery {
  readonly bookingId?: string
  readonly company?: string
  readonly building?: string
  readonly zone?: string
  readonly gate?: string
  readonly route?: string
  readonly level?: string
  readonly area?: string
  readonly offloadingEquipmentIds?: readonly string[]
}

export interface INotificationCompany {
  readonly id: string
  readonly name?: string
}

export interface INotificationOption {
  readonly id: string
  readonly name?: string
}

export interface INotificationUser {
  readonly id: string
  readonly firstName?: string
  readonly lastName?: string
  readonly email?: string
}

export interface INotificationPermitTypesStore {
  readonly actualTypes?: readonly INotificationPermitType[]
  getPermitTypeById(id: string): INotificationPermitType | undefined
}

export interface INotificationDeliveriesStore {
  readonly byId: {
    get(id: string): INotificationDelivery | undefined
  }
}

export interface INotificationCompaniesStore {
  readonly allCompanies?: readonly INotificationCompany[]
  getCompanyById(id: string): INotificationCompany | null | undefined
}

export interface INotificationProjectMembersStore {
  getById(id: string): INotificationUser | null | undefined
}

export interface INotificationLocationAttributesStore {
  readonly allLocationsWithoutEquipment?: readonly INotificationOption[]
  readonly offloadingEquipmentsStore?: {
    readonly list?: readonly INotificationOption[]
  }
  getById?(id: string): INotificationOption | null | undefined
}

export interface NotificationSchemaDeps {
  readonly permitTypesStore?: INotificationPermitTypesStore
  readonly permitBicStore?: Parameters<typeof isUserBicForCurrentStepDefault>[3]
  readonly deliveriesStore: INotificationDeliveriesStore
  readonly companiesStore: INotificationCompaniesStore
  readonly projectMembersStore: INotificationProjectMembersStore
  readonly locationAttributesStore?: INotificationLocationAttributesStore
  readonly getCurrentUserId: () => string | undefined
  readonly isUserBicForCurrentStep?: (
    userId: string,
    permit: INotificationPermit,
  ) => boolean
}

export interface INotificationRow {
  readonly id: string
  readonly notification: BaseNotification
  readonly readStatusCell: string
  readonly readStatusLabel: string
  readonly ntypeOptionIds: readonly string[]
  readonly ntypeLabels: readonly string[]
  readonly workflowType: string | null
  readonly workflowTypeLabel: string
  readonly workflowStatus: string | null
  readonly workflowStatusLabel: string
  readonly companyOptionIds: readonly string[]
  readonly companyLabels: readonly string[]
  readonly locationOptionIds: readonly string[]
  readonly locationLabels: readonly string[]
  readonly equipmentOptionIds: readonly string[]
  readonly equipmentLabels: readonly string[]
  readonly editorOptionIds: readonly string[]
  readonly editorLabels: readonly string[]
  readonly searchBlob: string
}

export const MENTION_TYPES: readonly NotificationType[] = [
  NotificationType.MessageReceived,
  NotificationType.PermitMessageReceived,
]

export interface NotificationStatusDescriptor {
  readonly id: string
  readonly label: string
  readonly permitStatus?: SitePermitStatus
}

const STATUS = {
  workflowSubmitted: {
    id: 'workflow-Submitted',
    label: 'Submitted',
    permitStatus: SitePermitStatus.Submitted,
  },
  workflowChanged: {
    id: 'workflow-Changed',
    label: 'Changed',
    permitStatus: SitePermitStatus.Changed,
  },
  workflowDenied: {
    id: 'workflow-Denied',
    label: 'Denied',
    permitStatus: SitePermitStatus.Denied,
  },
  workflowReviewed: {
    id: 'workflow-Reviewed',
    label: 'Reviewed',
    permitStatus: SitePermitStatus.Reviewed,
  },
  workflowApproved: {
    id: 'workflow-Approved',
    label: 'Approved',
    permitStatus: SitePermitStatus.Accepted,
  },
  workflowActivated: {
    id: 'workflow-Activated',
    label: 'Activated',
    permitStatus: SitePermitStatus.Active,
  },
  workflowOnSite: {
    id: 'workflow-On-site',
    label: 'On-site',
    permitStatus: SitePermitStatus.OnSite,
  },
  workflowToInspect: {
    id: 'workflow-To Inspect',
    label: 'To Inspect',
    permitStatus: SitePermitStatus.ToInspect,
  },
  workflowFailed: {
    id: 'workflow-Failed',
    label: 'Failed',
    permitStatus: SitePermitStatus.Failed,
  },
  workflowPassed: {
    id: 'workflow-Passed',
    label: 'Passed',
    permitStatus: SitePermitStatus.Passed,
  },
  workflowDone: {
    id: 'workflow-Done',
    label: 'Done',
    permitStatus: SitePermitStatus.Done,
  },
  workflowEnded: {
    id: 'workflow-Ended',
    label: 'Ended',
    permitStatus: SitePermitStatus.Ended,
  },
  workflowDeleted: {
    id: 'workflow-Deleted',
    label: 'Deleted',
    permitStatus: SitePermitStatus.Deleted,
  },
  deliveryRequested: { id: 'delivery-Requested', label: 'Requested' },
  deliveryScheduled: { id: 'delivery-Scheduled', label: 'Scheduled' },
  deliveryDelivering: { id: 'delivery-Delivering', label: 'Delivering' },
  deliveryPaused: { id: 'delivery-Paused', label: 'Paused' },
  deliveryOnHold: { id: 'delivery-On Hold', label: 'On Hold' },
  deliveryOnSite: { id: 'delivery-On Site', label: 'On Site' },
  deliveryPassedInspection: {
    id: 'delivery-Passed Inspection',
    label: 'Passed Inspection',
  },
  deliveryFailedInspection: {
    id: 'delivery-Failed Inspection',
    label: 'Failed Inspection',
  },
  deliveryDone: { id: 'delivery-Done', label: 'Done' },
  deliveryDenied: { id: 'delivery-Denied', label: 'Denied' },
  deliveryCanceled: { id: 'delivery-Canceled', label: 'Canceled' },
  deliveryChanged: { id: 'delivery-Changed', label: 'Changed' },
} as const satisfies Record<string, NotificationStatusDescriptor>

export const NOTIFICATION_STATUSES: readonly NotificationStatusDescriptor[] =
  Object.values(STATUS)

export const NOTIFICATION_STATUS_MAP: Partial<
  Record<NotificationType, string>
> = {
  [NotificationType.DeliveryRequested]: STATUS.deliveryRequested.id,
  [NotificationType.DeliveryRequestedByLocationClosure]:
    STATUS.deliveryRequested.id,
  [NotificationType.DeliveryScheduled]: STATUS.deliveryScheduled.id,
  [NotificationType.DeliveryDelivering]: STATUS.deliveryDelivering.id,
  [NotificationType.DeliveryPaused]: STATUS.deliveryPaused.id,
  [NotificationType.DeliveryOnhold]: STATUS.deliveryOnHold.id,
  [NotificationType.DeliveryOnsite]: STATUS.deliveryOnSite.id,
  [NotificationType.DeliveryPassedInspection]:
    STATUS.deliveryPassedInspection.id,
  [NotificationType.DeliveryFailedInspection]:
    STATUS.deliveryFailedInspection.id,
  [NotificationType.DeliveryDone]: STATUS.deliveryDone.id,
  [NotificationType.DeliveryIncompleteDone]: STATUS.deliveryDone.id,
  [NotificationType.DeliveryDenied]: STATUS.deliveryDenied.id,
  [NotificationType.DeliveryCanceled]: STATUS.deliveryCanceled.id,
  [NotificationType.DeliveryUpdated]: STATUS.deliveryChanged.id,
  [NotificationType.PermitSubmitted]: STATUS.workflowSubmitted.id,
  [NotificationType.PermitChanged]: STATUS.workflowChanged.id,
  [NotificationType.PermitDenied]: STATUS.workflowDenied.id,
  [NotificationType.PermitReviewed]: STATUS.workflowReviewed.id,
  [NotificationType.PermitAccepted]: STATUS.workflowApproved.id,
  [NotificationType.PermitActivated]: STATUS.workflowActivated.id,
  [NotificationType.PermitOnsite]: STATUS.workflowOnSite.id,
  [NotificationType.PermitToInspect]: STATUS.workflowToInspect.id,
  [NotificationType.PermitFailed]: STATUS.workflowFailed.id,
  [NotificationType.PermitPassed]: STATUS.workflowPassed.id,
  [NotificationType.PermitClosed]: STATUS.workflowDone.id,
  [NotificationType.PermitEnded]: STATUS.workflowEnded.id,
  [NotificationType.PermitDeleted]: STATUS.workflowDeleted.id,
}

export const STATUS_LABELS: Readonly<Record<string, string>> =
  Object.fromEntries(
    NOTIFICATION_STATUSES.map(status => [status.id, status.label]),
  )

export function notificationStatusIdForPermitStatus(
  permitStatus: SitePermitStatus,
): string | null {
  return (
    NOTIFICATION_STATUSES.find(status => status.permitStatus === permitStatus)
      ?.id ?? null
  )
}

export function createNotificationSchema(): ListSchema<INotificationRow> {
  const t = Localization.translator

  return {
    getId: row => row.id,
    fields: [
      {
        key: NOTIFICATION_FIELD.readStatus,
        label: t.readStatus || 'Read Status',
        value: row => row.readStatusCell,
        labelValue: row => row.readStatusLabel,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.notificationType,
        label: 'Notification Type',
        value: row => row.ntypeOptionIds,
        labelValue: row => row.ntypeLabels,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.workflowType,
        label: t.workflowType || 'Workflow Type',
        value: row => row.workflowType,
        labelValue: row => row.workflowTypeLabel,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.workflowStatus,
        label: t.workflowStatus || 'Workflow Status',
        value: row => row.workflowStatus,
        labelValue: row => row.workflowStatusLabel,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.company,
        label: t.company,
        value: row => row.companyOptionIds,
        labelValue: row => row.companyLabels,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.location,
        label: t.location,
        value: row => row.locationOptionIds,
        labelValue: row => row.locationLabels,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.equipment,
        label: t.equipment || 'Equipment',
        value: row => row.equipmentOptionIds,
        labelValue: row => row.equipmentLabels,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.editor,
        label: 'Editor',
        value: row => row.editorOptionIds,
        labelValue: row => row.editorLabels,
        facetable: true,
        searchable: false,
      },
      {
        key: NOTIFICATION_FIELD.searchBlob,
        label: 'Search',
        value: row => row.searchBlob,
      },
    ],
    bandLevels: [],
  }
}

export function readStatusLabel(optionId: string): string {
  const t = Localization.translator
  switch (optionId) {
    case READ_STATUS.unread:
      return t.unread_2form
    case READ_STATUS.read:
      return t.read_2form
    case READ_STATUS.archivedUnread:
      return `${t.archived} ${t.unread_2form}`
    case READ_STATUS.archivedRead:
      return `${t.archived} ${t.read_2form}`
    default:
      return optionId
  }
}

export function notificationTypeLabel(optionId: string): string {
  const t = Localization.translator
  switch (optionId) {
    case NOTIFICATION_TYPE.mention:
      return t.mention
    case NOTIFICATION_TYPE.bic:
      return 'Ball in Court'
    case NOTIFICATION_TYPE.siteComm:
      return t.siteCommunication
    case NOTIFICATION_TYPE.update:
      return t.updates
    default:
      return optionId
  }
}

export function getAuthorName(
  user:
    | {
        readonly firstName?: string
        readonly lastName?: string
        readonly email?: string
      }
    | null
    | undefined,
): string {
  if (!user) return ''
  if (!user.firstName && !user.lastName) return user.email || ''
  return `${user.firstName || ''} ${user.lastName || ''}`
}
